import React from 'react';
import { buttons, forms, layouts, textblocks } from 'seal-ui-components';
import ExpandablePanel from '@Components/UI/ExpandablePanel/ExpandablePanel';
import {
    DOCUMENT_SCORECARD,
    CLAUSE_LIBRARY,
    AUTHENTICATION,
    NOTIFICATION_HANDLER,
    DOCUMENT_METADATA,
    CONTRACT_SEARCH,
    FILTERS
} from '@Constants/types';
import {
    isCurrentSystemExpired,
    loginSuccessHandler,
    updateSealSystem,
    updateAllSealSystems,
    removeSealSystem
} from '@Actions/authentication';
import { LOG_TYPE, addLog } from '@Service/loggingService';
import validationHandler from '@Service/validationService';
import { STORAGE_TYPE, setInStorage, getFromStorage } from '@Service/storageService';
import { removeTrailingSlash, guid } from '@Utils/commonUtils';
import SystemPanel from './SystemPanelComponent/SystemPanel';
import { BackendContainer, Container, SystemPanelContainer } from './ConfigComponent.style';
import store from '@Store';

export class ConfigComponent extends React.Component {
    constructor(props) {
        super(props);

        // read all the seal systems from the end-users' local_storage
        let availableSystems = [];
        const sealSystems = getFromStorage('sealSystems', STORAGE_TYPE.LOCAL_STORAGE);
        if (sealSystems) {
            availableSystems = JSON.parse(sealSystems);
            updateAllSealSystems(availableSystems);
        }

        // initialise the state
        this.state = {
            sealSystems: availableSystems,
            selectedSystemId: '',
            friendlyName: '',
            AuthSectionState: 'Sso',
            systemUrl: '',
            panelChecked: false,
            closePanel: false,
            addFriendlyName: '',
            addSystemUrl: '',
            urlErrorMessage: [],
            nameErrorMessage: []
        };
    }

    // Ensure that the selected system is ticked appropriately when this component loads
    componentDidMount() {
        const { sealSystems } = this.state;
        const { activeSystem } = this.props;

        const id = getFromStorage('selectedSystemId', STORAGE_TYPE.LOCAL_STORAGE);
        const url = getFromStorage('seal_url', STORAGE_TYPE.LOCAL_STORAGE);

        if (sealSystems.length < 1)
            this.setState({
                panelChecked: true
            });
        if (id) if (url !== activeSystem) this.setupSystemsPanel(id);
    }

    // Ensure that the friendly name in the state equals the value entered by the end-user
    setFriendlyName = e => {
        this.setState({
            friendlyName: e.target.value
        });
    };

    // Ensure that the system Url in the state equals the value entered by the end-user
    setUrl = e => {
        this.setState({
            systemUrl: removeTrailingSlash(e.target.value.trim())
        });
    };

    setAddFriendlyName = e => {
        this.setState({
            addFriendlyName: e.target.value,
            friendlyName: e.target.value
        });
    };

    // Ensure that the system Url in the state equals the value entered by the end-user
    setAddUrl = e => {
        this.setState({
            addSystemUrl: removeTrailingSlash(e.target.value.trim()),
            systemUrl: removeTrailingSlash(e.target.value.trim())
        });
    };

    /* Locate the system in the systems collection via the system Id.  Once located, update this component
        components local state to tick the selected system */
    async setupSystemsPanel(id) {
        try {
            let sealSystems = [];

            const availableSystems = getFromStorage('sealSystems', STORAGE_TYPE.LOCAL_STORAGE);
            if (availableSystems) {
                sealSystems = JSON.parse(availableSystems);
            }
            const { logout } = this.props;

            if (sealSystems) {
                let selectedSystemFriendlyName = '';
                let selectedSystemUrl = '';
                let selectedAuthToken = '';
                let selectedAuthenticationType = '';
                let selectedTokenIssueTime = '';
                let selectedAuthSection = '';

                // find the system in the systems collection based on the system Id
                const selectedSystem = sealSystems.find(s => s.id === id);
                if (selectedSystem) {
                    selectedSystemFriendlyName = selectedSystem.friendlyName;
                    selectedSystemUrl = selectedSystem.sealUrl;
                    selectedAuthToken = selectedSystem.authToken;
                    selectedAuthenticationType = selectedSystem.authType;
                    selectedTokenIssueTime = selectedSystem.tokenIssueTime;
                    selectedAuthSection =
                        selectedSystem.authSection.toLowerCase() === 'sso' ||
                        selectedSystem.authSection.toLowerCase() === 'credentials'
                            ? selectedSystem.authSection
                            : 'Sso';
                }

                updateSealSystem(
                    selectedSystemUrl,
                    selectedSystemFriendlyName,
                    'v3',
                    selectedAuthToken,
                    selectedAuthenticationType,
                    selectedTokenIssueTime,
                    selectedAuthSection
                );

                // Set this components local state to tick the selected system
                setInStorage('selectedSystemId', id, STORAGE_TYPE.LOCAL_STORAGE);

                // store the selected seal url in the end-users' local storage
                setInStorage('seal_url', selectedSystemUrl, STORAGE_TYPE.LOCAL_STORAGE);

                // store the selected friendly name in the end-users' local storage
                setInStorage(
                    'friendly_name',
                    selectedSystemFriendlyName,
                    STORAGE_TYPE.LOCAL_STORAGE
                );
                setInStorage('authToken', selectedAuthToken, STORAGE_TYPE.LOCAL_STORAGE);
                setInStorage('authType', selectedAuthenticationType, STORAGE_TYPE.LOCAL_STORAGE);

                this.setState({
                    selectedSystemId: id,
                    systemUrl: selectedSystemUrl,
                    friendlyName: selectedSystemFriendlyName,
                    AuthSectionState: selectedAuthSection
                });

                // store the selected seal system Id in the end-users' local storage

                if (!isCurrentSystemExpired(id)) {
                    loginSuccessHandler();
                } else {
                    // auth token has exceed it's 4 hour lifespan, so logout this system
                    logout(true);
                }
            }
        } catch (err) {
            addLog(LOG_TYPE.ERROR, 'ConfigComponent.OnSystemClick', err);
            store.dispatch({
                type: NOTIFICATION_HANDLER.ERROR,
                payload: err
            });
        }
    }

    // get the system Id from the click event and update the state accordingly for the newly selected system
    OnSystemClick = id => {
        try {
            const { selectedSystemId } = this.state;

            store.dispatch({ type: CLAUSE_LIBRARY.CLEAR_CLAUSE_LIBRARY });
            store.dispatch({ type: CONTRACT_SEARCH.SEARCH_CLEAR });

            if (selectedSystemId !== id) {
                store.dispatch({ type: DOCUMENT_SCORECARD.CLEAR_DIGAPPS });
                setInStorage('mockSealVersion', '', STORAGE_TYPE.LOCAL_STORAGE);
                store.dispatch({ type: AUTHENTICATION.UN_MOCK_SEAL_VERSION });

                const { clearScoreCard, logout } = this.props;
                logout(false);
                clearScoreCard();

                if (id) this.setupSystemsPanel(id);

                // check if the docId was sent to this new system
                const sealUrlSubmittedOn = getFromStorage('sealUrl', STORAGE_TYPE.PROPERTY_BAG);
                const selectedSystemUrl = getFromStorage('seal_url', STORAGE_TYPE.LOCAL_STORAGE);

                if (sealUrlSubmittedOn !== selectedSystemUrl) {
                    store.dispatch({
                        type: DOCUMENT_METADATA.SWITCH_SYSTEM,
                        payload: { isSubmitted: false }
                    });
                }

                store.dispatch({ type: FILTERS.RESET_FILTERS });
            }
        } catch (err) {
            addLog(LOG_TYPE.ERROR, 'ConfigComponent.OnSystemClick', err);
            store.dispatch({
                type: NOTIFICATION_HANDLER.ERROR,
                payload: err
            });
        }
    };

    // Add a new system to the collection
    // Systems are stored in both this components local state and the end-user's local storage
    addSystem = () => {
        try {
            const {
                sealSystems,
                addFriendlyName,
                addSystemUrl,
                id,
                systemUrl,
                friendlyName
            } = this.state;
            const { logout } = this.props;
            const validationObject = validationHandler(
                id,
                addSystemUrl,
                addFriendlyName,
                sealSystems
            );

            if (validationObject.valid) {
                logout(false);

                if (sealSystems) {
                    const newSystemId = guid();

                    // friendly names must be unique
                    const existingSystem = sealSystems.find(s => s.friendlyName === friendlyName);
                    if (!existingSystem) {
                        const newSystem = {
                            id: newSystemId,
                            sealUrl: systemUrl,
                            friendlyName,
                            authToken: '',
                            authType: '',
                            tokenIssueTime: '',
                            authSection: ''
                        };

                        // add new system to the collection
                        sealSystems.push(newSystem);

                        // reset this components local state, un-tick any previously selected systems
                        this.setState({
                            sealSystems,
                            friendlyName: '',
                            systemUrl: '',
                            selectedSystemId: ''
                        });

                        // stash the systems collection in the end-user's local storage
                        setInStorage(
                            'sealSystems',
                            JSON.stringify(sealSystems),
                            STORAGE_TYPE.LOCAL_STORAGE
                        );

                        // clear down the selected system from the end-user's local storage
                        setInStorage('seal_url', '', STORAGE_TYPE.LOCAL_STORAGE);
                        setInStorage('friendly_name', '', STORAGE_TYPE.LOCAL_STORAGE);

                        this.setState({
                            closePanel: true
                        });

                        // automatically select the system that has just been added
                        this.setupSystemsPanel(newSystemId);
                        this.SystemPanelButtonClick();
                    }
                }
            } else {
                this.setState({
                    nameErrorMessage: [validationObject.name],
                    urlErrorMessage: [validationObject.url]
                });
            }
        } catch (err) {
            addLog(LOG_TYPE.ERROR, 'ConfigComponent.addSystem', err);
            store.dispatch({
                type: NOTIFICATION_HANDLER.ERROR,
                payload: err
            });
        }
    };

    editSealSystem = (id, systemAddressInput, systemNameInput) => {
        try {
            const { sealSystems, selectedAuthType, AuthSectionState } = this.state;
            const { logout } = this.props;

            const existingSystem = sealSystems.find(s => s.id === id);
            if (existingSystem.sealUrl !== systemAddressInput) logout(false);
            const newSystem = {
                id,
                sealUrl: systemAddressInput,
                friendlyName: systemNameInput,
                authToken: `${systemAddressInput === (existingSystem.sealUrl || '') &&
                    existingSystem.authToken}`,
                authType: `${systemAddressInput === (existingSystem.sealUrl || '') &&
                    existingSystem.authType}`,
                tokenIssueTime: `${systemAddressInput === (existingSystem.sealUrl || '') &&
                    existingSystem.tokenIssueTime}`,
                authSection: `${systemAddressInput === (existingSystem.sealUrl || '') &&
                    existingSystem.authSection}`
            };

            const newSystems = sealSystems;
            newSystems.splice(sealSystems.indexOf(existingSystem), 1, newSystem);

            setInStorage('sealSystems', JSON.stringify(newSystems), STORAGE_TYPE.LOCAL_STORAGE);
            setInStorage('seal_url', newSystem.sealUrl, STORAGE_TYPE.LOCAL_STORAGE);
            setInStorage('friendly_name', newSystem.friendlyName, STORAGE_TYPE.LOCAL_STORAGE);

            removeSealSystem(newSystem.sealUrl);
            updateSealSystem(
                newSystem.sealUrl,
                newSystem.friendlyName,
                'v3',
                newSystem.authToken,
                selectedAuthType,
                newSystem.tokenIssueTime,
                AuthSectionState
            );

            this.setState({
                sealSystems: newSystems
            });
        } catch (err) {
            addLog(LOG_TYPE.ERROR, 'ConfigComponent.editSealSystem', err);
            store.dispatch({
                type: NOTIFICATION_HANDLER.ERROR,
                payload: err
            });
        }
    };

    SealSystemForToggleSwitch = (input, id) => {
        try {
            const { sealSystems, systemUrl } = this.state;
            const existingSystem = sealSystems.find(s => s.id === id);

            const newSystem = {
                id,
                sealUrl: existingSystem.sealUrl,
                friendlyName: existingSystem.friendlyName,
                authToken: existingSystem.authToken,
                authType: existingSystem.authType,
                tokenIssueTime: existingSystem.tokenIssueTime,
                authSection: `${systemUrl === (existingSystem.sealUrl || '') && input}`
            };

            const newSystems = sealSystems;
            newSystems.splice(sealSystems.indexOf(existingSystem), 1, newSystem);
            this.setState(
                {
                    sealSystems: newSystems,
                    AuthSectionState: input
                },
                () => {
                    // removeSealSystem(newSystem.sealUrl);
                    updateSealSystem(
                        newSystem.sealUrl,
                        newSystem.friendlyName,
                        'v3',
                        newSystem.authToken,
                        newSystem.tokenIssueTime,
                        input
                    );
                    setInStorage(
                        'sealSystems',
                        JSON.stringify(newSystems),
                        STORAGE_TYPE.LOCAL_STORAGE
                    );
                }
            );
        } catch (err) {
            addLog(LOG_TYPE.ERROR, 'ConfigComponent.editSealSystem', err);
            store.dispatch({
                type: NOTIFICATION_HANDLER.ERROR,
                payload: err
            });
        }
    };

    toggleAuthType = (input, id) => {
        store.dispatch({ type: AUTHENTICATION.CLEAR_ERROR });
        this.SealSystemForToggleSwitch(input, id);
    };

    // remove a system from the collection
    // Systems are stored in both this components local state and the end-user's local storage
    removeSystem = id => {
        try {
            const { logout } = this.props;

            logout(false);

            if (id) {
                const sealSystems = getFromStorage('sealSystems', STORAGE_TYPE.LOCAL_STORAGE);
                if (sealSystems) {
                    const systems = JSON.parse(sealSystems);
                    const existingSystem = systems.find(s => s.id === id);

                    // copy the system collections to another array whilst excluding the system we wish to remove
                    const newSystems = [];
                    systems.forEach(s => {
                        if (s.id !== id) {
                            newSystems.push(s);
                        }
                    });

                    // stash the systems collection in the end-user's local storage
                    setInStorage(
                        'sealSystems',
                        JSON.stringify(newSystems),
                        STORAGE_TYPE.LOCAL_STORAGE
                    );

                    // reset this components local state, un-tick any previously selected systems

                    removeSealSystem(existingSystem.sealUrl);

                    this.setState(
                        {
                            sealSystems: newSystems,
                            friendlyName: '',
                            systemUrl: ''
                        },
                        () => {
                            if (newSystems.length === 0) this.SystemPanelButtonClick();
                        }
                    );

                    // clear down the selected system from the end-user's local storage
                    setInStorage('seal_url', '', STORAGE_TYPE.LOCAL_STORAGE);
                    setInStorage('friendly_name', '', STORAGE_TYPE.LOCAL_STORAGE);
                }
            }
        } catch (err) {
            addLog(LOG_TYPE.ERROR, 'ConfigComponent.removeSystem', err);
            store.dispatch({
                type: NOTIFICATION_HANDLER.ERROR,
                payload: err
            });
        }
    };

    SystemPanelButtonClick = () => {
        const { panelChecked } = this.state;

        this.setState({
            panelChecked: !panelChecked,
            addFriendlyName: '',
            addSystemUrl: '',
            urlErrorMessage: [],
            nameErrorMessage: []
        });
    };

    IconProps = () => {
        const { panelChecked } = this.state;
        if (panelChecked) {
            return { iconName: 'ChevronUpSmall' };
        }
        return { iconName: 'ChevronDownSmall' };
    };

    render() {
        const {
            sealSystems,
            selectedSystemId,
            panelChecked,
            addFriendlyName,
            addSystemUrl,
            urlErrorMessage,
            nameErrorMessage,
            AuthSectionState,
            closePanel
        } = this.state;

        const { logout, activeSystem } = this.props;
        const addButtonEnabled = !addFriendlyName || !addSystemUrl;

        return (
            <Container>
                <ExpandablePanel
                    title="Add new system"
                    controlledPanelCheck={panelChecked}
                    closePanel={closePanel}
                    onToggleClick={() => this.SystemPanelButtonClick()}
                >
                    <forms.TextField
                        label="System name"
                        id="new_system_name"
                        name="seal-url-friendly-name"
                        placeholder="Name your system"
                        onChange={e => this.setAddFriendlyName(e)}
                        value={addFriendlyName}
                        errors={nameErrorMessage}
                    />
                    <forms.TextField
                        label="Server address"
                        id="new_server_address"
                        name="seal-url"
                        placeholder="https://sealserver.seal-software.net"
                        onChange={e => this.setAddUrl(e)}
                        value={addSystemUrl}
                        errors={urlErrorMessage}
                    />
                    <layouts.HorizontalBox>
                        <layouts.BoxSpacer />
                        <buttons.Button
                            appearance="primary"
                            size="large"
                            id="add_system_button"
                            disabled={addButtonEnabled}
                            onClick={() => this.addSystem()}
                        >
                            Add system
                        </buttons.Button>
                    </layouts.HorizontalBox>
                </ExpandablePanel>
                {sealSystems.length > 0 && (
                    <SystemPanelContainer>
                        <textblocks.Text className="system_panel_title">
                            Log in to system
                        </textblocks.Text>
                        <BackendContainer>
                            <SystemPanel
                                setUrl={this.setUrl}
                                setFriendlyName={this.setFriendlyName}
                                sealSystems={sealSystems}
                                activeSystem={activeSystem}
                                selectedSystemId={selectedSystemId}
                                OnSystemClick={this.OnSystemClick}
                                editSealSystem={this.editSealSystem}
                                removeSystem={this.removeSystem}
                                toggleAuthType={this.toggleAuthType}
                                AuthSectionState={AuthSectionState}
                                logout={logout}
                            />
                        </BackendContainer>
                    </SystemPanelContainer>
                )}
            </Container>
        );
    }
}

export default ConfigComponent;
